import { session } from './session'
import { usesFirebaseSession } from './session-config'
import { cfAxios } from '@/utils/init-axios.js'

/**
 * Read a user's ui-preference bucket. Several views own a key inside it and a
 * PUT replaces the whole object, so a 404 (no bucket yet) yields {} while every
 * other failure propagates — a writer must abort rather than merge against a
 * bucket it failed to read.
 */
export async function readPreferenceBucket(
  url: string
): Promise<Record<string, any>> {
  let data
  try {
    data = (await cfAxios.get(url)).data
  } catch (error) {
    if ((error as any)?.response?.status === 404) return {}
    throw error
  }
  if (data === null || data === undefined || data === '') return {}
  // A misrouted 200 would spread into character keys and corrupt the bucket.
  if (typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('ui-preference returned a non-object body')
  }

  return data
}

let bucketWrites: Promise<unknown> = Promise.resolve()

/**
 * Serialise a read-modify-write on the shared bucket. Two writers own a key in
 * it — the customer-list column preset and the sidebar's starred reports — and a
 * PUT replaces the whole object, so overlapping pairs drop one of the keys.
 */
export function queueBucketWrite<T>(task: () => Promise<T>): Promise<T> {
  const generation = session.generation
  const run = () => {
    if (usesFirebaseSession()) session.assertCurrent(generation)

    return task()
  }
  const result = bucketWrites.then(run, run)
  // One writer's failure must not reject the next writer's turn.
  bucketWrites = result.then(
    () => undefined,
    () => undefined
  )

  return result
}

/** Test seam: the queue is module state, so a test must be able to clear it. */
export function resetBucketWriteQueue() {
  bucketWrites = Promise.resolve()
}
